import React, {useMemo, useState} from 'react';
import {
  Menu,
  ListItemIcon,
  IconButton,
  Box,
  Typography,
  Checkbox,
} from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import {SVG} from '../../constants/assetLists';
import AppSwitch from './AppSwitch';
import AppMUIMenuItem from './AppMUIMenuItem';
import {useDebounce} from '../../hooks/useDebunce';
import AppTextInput from './AppTextInput';
import {highlightSearchMatch} from '../../pages/operations/components/AnnouncementsAndTasks/components/AnnouncementCard';

type Props = {
  anchorEl: null | HTMLElement;
  open: boolean;
  onClose: () => void;
  onOpen: (event: React.MouseEvent<HTMLElement>) => void;
  showSwitch?: boolean;
  switchChecked?: boolean;
  onSwitchChange?: () => void;
  switchLabel?: string;
  items: {
    title: string;
    value: string;
    icon?: string;
    permission?: string;
    disabled?: boolean;
  }[];
  onItemClick: (val: string) => void;
  selectedItem?: string;
  minWidth?: number;
  menuIcon?: React.ReactNode;
  showDivider?: boolean;
  dividerIndex?: number;
  itemIconWidth?: number;
  itemIconHeight?: number;
  disableMenuIcon?: boolean;
  searchable?: boolean;
};

function AppMenu({
  anchorEl,
  open,
  onClose,
  onOpen,
  showSwitch,
  switchChecked,
  onSwitchChange,
  switchLabel,
  items,
  onItemClick,
  selectedItem,
  minWidth,
  menuIcon,
  showDivider,
  dividerIndex,
  itemIconWidth = 18,
  itemIconHeight = 18,
  disableMenuIcon = false,
  searchable = false,
}: Props) {
  const [searchText, setSearchText] = useState('');

  const debouncedSearch = useDebounce(searchText, 500);

  const filteredItems = useMemo(() => {
    if (!searchable) return items;

    return items.filter(item =>
      item.title.toLowerCase().includes(debouncedSearch.toLowerCase()),
    );
  }, [items, debouncedSearch, searchable]);

  return (
    <>
      {!menuIcon ? (
        <IconButton
          size="small"
          disabled={disableMenuIcon}
          onClick={e => {
            e.stopPropagation();
            onOpen(e as React.MouseEvent<HTMLElement>);
          }}>
          {menuIcon ? menuIcon : <MoreVertIcon />}
        </IconButton>
      ) : (
        <Box
          sx={{display: 'inline-flex', alignItems: 'center'}}
          role="button"
          tabIndex={disableMenuIcon ? -1 : 0}
          aria-disabled={disableMenuIcon || undefined}
          onClick={e => {
            e.stopPropagation();
            onOpen(e as React.MouseEvent<HTMLElement>);
          }}>
          {menuIcon}
        </Box>
      )}

      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={e => {
          (e as any)?.stopPropagation?.();
          onClose();
        }}
        slotProps={{
          paper: {
            sx: {
              minWidth: minWidth || 209,
              p: 1,
              color: '#000',
              border: '1px solid #707070',
            },
          },
        }}>
        {searchable && (
          <Box sx={{mb: 1}}>
            <AppTextInput
              placeholder="חיפוש"
              className="bg-transparent !rounded-[28px] pl-16"
              value={searchText}
              onChange={e => setSearchText(e.target.value)}
              endIcon={
                <div className="flex items-center gap-2">
                  {searchText && (
                    <button
                      onClick={() => setSearchText('')}
                      className="w-[12px] h-[12px] hover:bg-gray-200">
                      <img
                        src={SVG.closeDarkIcon}
                        alt="Clear"
                        className="w-full h-full object-contain"
                      />
                    </button>
                  )}
                  <div
                    className={`w-[24px] h-[24px] flex justify-center items-center rounded-full ${
                      searchText && 'bg-aqua'
                    }`}>
                    <img
                      src={SVG.searchIcon}
                      alt="Search"
                      className="w-[18px] h-[18px] object-contain"
                    />
                  </div>
                </div>
              }
            />
          </Box>
        )}

        {showSwitch && (
          <Box>
            <Box sx={{display: 'flex', alignItems: 'center', gap: 1, mb: 1}}>
              <AppSwitch checked={switchChecked} onChange={onSwitchChange} />
              <Typography color="#000">{switchLabel}</Typography>
            </Box>

            <div className="w-full h-[1px] bg-gray50" />
          </Box>
        )}

        {filteredItems.map((item, idx) => {
          const hasIcon = item?.icon && item?.icon != 'checkbox';
          const isCheckbox = item?.icon == 'checkbox';
          const isSelected = selectedItem == item.value;

          return (
            <Box key={item.value}>
              <AppMUIMenuItem
                selected={isSelected}
                disabled={item.disabled}
                onClick={e => {
                  e.stopPropagation();
                  onItemClick(item.value);
                  onClose();
                }}>
                {hasIcon && (
                  <ListItemIcon>
                    <Box sx={{width: itemIconWidth, height: itemIconHeight}}>
                      <img
                        src={item.icon}
                        alt="icon"
                        className="w-full h-full object-contain"
                      />
                    </Box>
                  </ListItemIcon>
                )}
                {isCheckbox && (
                  <ListItemIcon>
                    <Checkbox
                      size="small"
                      color="secondary"
                      sx={{padding: 0}}
                      checked={isSelected}
                    />
                  </ListItemIcon>
                )}
                {isSelected && !hasIcon && !isCheckbox && (
                  <ListItemIcon>
                    <Box sx={{width: 18, height: 18}}>
                      <img
                        src={SVG.checkGrayIcon}
                        alt="icon"
                        className="w-full h-full object-contain"
                      />
                    </Box>
                  </ListItemIcon>
                )}
                <span>{highlightSearchMatch(item.title, searchText)}</span>
              </AppMUIMenuItem>

              {showDivider && idx == dividerIndex && (
                <div className="w-full h-[1px] bg-gray50" />
              )}
            </Box>
          );
        })}
      </Menu>
    </>
  );
}

export default AppMenu;
